import { useEffect, useState } from 'react'
import { adminApi } from '../lib/api'
import { transformArray } from '../lib/transformers'
import { Plus, Trash2, Edit, Shield, ShieldCheck } from 'lucide-react'
import type { Admin } from '../types'
import { useAuthStore } from '../store/authStore'

export default function AdminsPage() {
  const currentAdmin = useAuthStore((state) => state.admin)
  const [admins, setAdmins] = useState<Admin[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (currentAdmin?.role !== 'super_admin') {
      return
    }
    loadAdmins()
  }, [currentAdmin])

  const loadAdmins = async () => {
    setLoading(true)
    try {
      const response = await adminApi.getAdmins()
      let rawData = []
      if (!response.data) {
        rawData = []
      } else if (Array.isArray(response.data)) {
        rawData = response.data
      } else if (typeof response.data === 'object') {
        // API returns {success: true, data: {admins: [], pagination: {}}}
        rawData = response.data?.data?.admins || response.data?.admins || response.data?.data || response.data?.items || []
      }
      // Transform snake_case to camelCase
      const data = transformArray<Admin>(Array.isArray(rawData) ? rawData : [])
      setAdmins(data)
    } catch (error) {
      console.error('Failed to load admins:', error)
      setAdmins([])
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this admin?')) return

    try {
      await adminApi.deleteAdmin(id)
      loadAdmins()
    } catch (error) {
      console.error('Failed to delete admin:', error)
    }
  }

  if (currentAdmin?.role !== 'super_admin') {
    return (
      <div className="text-center py-12">
        <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">You don't have permission to access this page</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admins</h1>
          <p className="text-gray-600 mt-1">Manage admin users and permissions</p>
        </div>
        <button
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Create Admin
        </button>
      </div>

      <div className="card">
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : admins.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No admins found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-3 font-medium text-gray-700">Username</th>
                  <th className="pb-3 font-medium text-gray-700">Email</th>
                  <th className="pb-3 font-medium text-gray-700">Role</th>
                  <th className="pb-3 font-medium text-gray-700">2FA</th>
                  <th className="pb-3 font-medium text-gray-700">Last Login</th>
                  <th className="pb-3 font-medium text-gray-700">Status</th>
                  <th className="pb-3 font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {admins.map((admin) => (
                  <tr key={admin.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="py-3">
                      <div className="flex items-center gap-2">
                        {admin.role === 'super_admin' ? (
                          <ShieldCheck className="h-4 w-4 text-primary-600" />
                        ) : (
                          <Shield className="h-4 w-4 text-gray-400" />
                        )}
                        <span className="font-medium text-gray-900">{admin.username}</span>
                      </div>
                    </td>
                    <td className="py-3 text-sm text-gray-700">{admin.email}</td>
                    <td className="py-3">
                      <span className={`badge ${
                        admin.role === 'super_admin' ? 'badge-success' : 'badge-info'
                      }`}>
                        {admin.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                      </span>
                    </td>
                    <td className="py-3">
                      <span className={`badge ${
                        admin.twoFactorEnabled ? 'badge-success' : 'badge-warning'
                      }`}>
                        {admin.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td className="py-3 text-sm text-gray-700">
                      {admin.lastLogin ? new Date(admin.lastLogin).toLocaleString() : 'Never'}
                    </td>
                    <td className="py-3">
                      <span className={`badge ${
                        admin.isActive ? 'badge-success' : 'badge-danger'
                      }`}>
                        {admin.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-3">
                      <div className="flex gap-2">
                        <button className="p-1 text-secondary-blue hover:bg-blue-50 rounded">
                          <Edit className="h-4 w-4" />
                        </button>
                        {admin.id !== currentAdmin.id && (
                          <button
                            onClick={() => handleDelete(admin.id)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
